import { useCallback, useEffect, useRef, useState } from 'react'

export type ContextsPhase = 'loading' | 'ready' | 'error'

/**
 * One ASF surveillance area's heatmap, as `/api/contexts` reports it.
 *
 * `id` is Tanger's own context id — it goes back to the API in the layer
 * request path and is the only thing that decides which area's heatmap comes
 * back. `name` is display text, sourced from `configuration/asf-fin`'s
 * `heatmapContextNames`, and falls back server-side to the id when no name is
 * configured (a visible "someone should name this" rather than a blank).
 */
export interface HeatmapContext {
  id: string
  name: string
}

type Call = <T>(path: string) => Promise<T>

// Mirrors useAreas/useBaseMap's shape exactly, including the latest-ref
// pattern below; the call is injected so a test can drive the hook without
// mounting an auth provider.
export function useContexts(call: Call) {
  const [phase, setPhase] = useState<ContextsPhase>('loading')
  const [contexts, setContexts] = useState<HeatmapContext[]>(EMPTY)
  const [attempt, setAttempt] = useState(0)

  // `call` is not a dependency of the effect below — same reasoning as
  // useAreas: useApiFetch's returned function is a useCallback keyed on live
  // auth state, so re-running on its identity change would restart the fetch
  // on every token refresh.
  const callRef = useRef(call)
  callRef.current = call

  useEffect(() => {
    let cancelled = false
    setPhase('loading')
    callRef.current<HeatmapContext[]>('/contexts')
      .then((res) => {
        if (!cancelled) {
          // Server order, untouched: it is exportGroups' order, which is the
          // order an operator chose for the areas.
          setContexts(res)
          setPhase('ready')
        }
      })
      .catch(() => {
        // Like useAreas, this must not stop the map from drawing: the base
        // map is independent, and an empty context list renders as "no
        // heatmap yet" rather than a broken screen.
        if (!cancelled) setPhase('error')
      })
    return () => {
      cancelled = true
    }
  }, [attempt])

  const retry = useCallback(() => setAttempt((n) => n + 1), [])
  return { phase, contexts, retry }
}

// A module-level constant rather than a fresh `[]` per render: MapScreen keys
// one poller per context off this array, and a new empty reference on every
// render would remount them all.
const EMPTY: HeatmapContext[] = []
